
// region JS Imports

// region External Modules
import u from "umbrellajs"
import _template from "lodash/template"
import Promise from "plugins/bluebird/index"
import L from "plugins/leaflet/index"
// endregion External Modules

// Private Modules
import {Page} from "app/multi_device"

import AppMapZoomLevels from "app/map/zoom_levels"
import LeafletMapTileLayer from "app/map/leaflet/map_tile_layer"
import LeafletSettings from "app/map/leaflet/settings"

import Geometry_Coordinates_ValueFromWgs84ToGsj02Conversion_Op from "app/geometry/coordinates/value_from_wgs84_to_gsj02_conversion/operation"

import {
  q_ajax_3,
} from "app/utils/q_ajax/q_ajax_3"
// Private Modules

// region Public Components
import Sidebar from "components/map/sidebar/index"
// endregion Public Components

// region Private Components
import marker_template from "./marker.html._tpl"
const marker_tpl = _template(marker_template)
// endregion Private Components

// endregion JS Imports


interface InputSelector {
  scope?:  string | Element
  main?:   string
  btn?:    string
}
const default_selector = {
  scope:  ".cell--cities--public-transport--info",
  main:   "#map-canvas__listing-transit",
  btn:    ".cell--cities--public-transport--info__load-route-data-button",
}


// # Internal
let buildingMapMarker = null
const public_transport_route_data_cache = {} as {[record_id: string]: RouteDataEntry}

const route_groups = {} as {[record_id: string]: L.LayerGroup}


// ###
// Set map marker to show building location
// ###
const setMapMarker = (market_lat_lng: L.LatLng, data: {text: string}, map_layer: L.Map) => {
  buildingMapMarker = L.marker(
    market_lat_lng,
    {
      interactive:  false,
      riseOnHover:  true,

      icon: L.divIcon({
        className:  "map-marker-sign-with-arrow-container",
        html:       marker_tpl({text: data.text}),
      }),
    },
  );
  (buildingMapMarker as any).data = data

  map_layer.addLayer(buildingMapMarker)
}



interface RouteDataEntry {
  record: {
    transport_type_identifier: string
    hex_color_code: null | string

    public_transport_stops: Array<{
      name: string
      latitude: number | string
      longitude: number | string
    }>
  }
}


const load_route_data_promise = (public_transport_route_id: string): Promise<RouteDataEntry> => {
  return new Promise((resolve) => {
    if (public_transport_route_data_cache[public_transport_route_id] != null){
      return resolve(public_transport_route_data_cache[public_transport_route_id])
    }

    Promise.resolve(
      import(
        "plugins/js-routes"
      )
    )
    .then((Routes) => {
      q_ajax_3({
        url:      Routes.show_many_api_web_app_public_transport_data_routes_map_data_entries_path({
          public_transport_route_id: public_transport_route_id,
          use_baidu_maps:            LeafletSettings.get_use_baidu_maps(),
        }),
        method:   "GET",
        dataType: "JSON",
      })
      .then(({data}) => {
        public_transport_route_data_cache[public_transport_route_id] = data.data
        resolve(data.data)
      })
      .done()
    })
    .done()
  })
}

const load_route = (public_transport_route_id: string, map_layer: L.Map) => {
  const route_layer_group = route_groups[public_transport_route_id] != null ?
    route_groups[public_transport_route_id] : L.layerGroup()

  return load_route_data_promise(public_transport_route_id)
  .then((data) => {
    const routeSegments = [] as L.LatLng[]

    const $transit_route_button = u(`.cell--cities--public-transport--info--route-${public_transport_route_id}`)

    // # Fallback icon
    let transport_type_icon = "fa fa-car"
    switch (data.record.transport_type_identifier){
      case "train":
      case "tram":
        transport_type_icon = "fa fa-train"
        break
      case "bus":
      case "minibus":
        transport_type_icon = "fa fa-bus"
        break
      case "ferry":
        transport_type_icon = "fa fa-ship"
        break
    }
    const polyline_hex_color_code = data.record.hex_color_code || "#444444"

    // # if already displayed, then don"t send request and remove layer instead
    if (map_layer.hasLayer(route_layer_group)){
      map_layer.removeLayer(route_layer_group)
      $transit_route_button.removeClass("active")
    }
    else {
      data.record.public_transport_stops.forEach(({name, latitude, longitude}) => {
        const lat = typeof latitude === "number" ? latitude : parseFloat(latitude)
        const lng = typeof longitude === "number" ? longitude : parseFloat(longitude)

        const stopLatLng = L.latLng(lat, lng)

        const markerOptions = {
          icon: L.divIcon({
            className:  "map-marker-public-transport",
            html:       `<div><i class="${transport_type_icon}"></i></div>`,
          }),
          riseOnHover: true,
        }


        const marker = L.marker(
          stopLatLng,
          markerOptions
        )

        marker.bindTooltip(name, {
          className: "medium",
        })

        routeSegments.push(stopLatLng)

        route_layer_group.addLayer(marker)
      })



      const route = L.polyline([routeSegments], {
        color: polyline_hex_color_code,
      })

      route_layer_group.addLayer(route)

      route_groups[public_transport_route_id] = route_layer_group
      map_layer.addLayer(route_layer_group)
      $transit_route_button.addClass("active")
    }
  })
}



const init = (selector?: InputSelector): Promise<L.Map[]> => {
  const sele = Object.assign({}, default_selector, selector)
  const promises = u(sele.scope).nodes.map((el): Promise<L.Map> => {
    const $el = u(el)
    const $mapCanvas = $el.find(sele.main)
    if ($mapCanvas.length !== 1) {
      return Promise.reject(new Error(`$mapCanvas.length != 1 <${$mapCanvas.length}>`))
    }

    const lat = parseFloat($mapCanvas.data("lat"))
    const lng = parseFloat($mapCanvas.data("lng"))
    let   myCenter = L.latLng(lat, lng)

    if (LeafletSettings.get_use_baidu_maps()) {
      const baidu_latlng = (new Geometry_Coordinates_ValueFromWgs84ToGsj02Conversion_Op({
        latitude:   lat,
        longitude:  lng,
      })).call()

      myCenter = L.latLng(baidu_latlng.latitude, baidu_latlng.longitude)
    }


    Sidebar.initJSMapSidebarToggle( ".js-map-sidebar-toggle")

    // If we add map tile layer AFTER creating map object
    // The location of tile layer is drifted on load
    // To workaround this we create tile layer first and
    // pass it on map object creation
    return LeafletMapTileLayer.getNewTileLayerPromise()
    .then((map_tile_layer) => {
      const transit_map: L.Map = LeafletSettings.get_use_baidu_maps() ?
        L.map("map-canvas__listing-transit", {
          center:   myCenter,
          zoom:     15,
          minZoom:  10,
          maxZoom:  AppMapZoomLevels.max,

          scrollWheelZoom:  false,
          // Using custom zoom control
          zoomControl:      false,

          attributionControl: false,

          dragging: (Page.isForDesktop() || Page.isForTablet()),
          // `tap` is also required to be disabled for mobile
          // in order to allow page scroll
          // https://github.com/Leaflet/Leaflet/issues/5425#issuecomment-342055520
          tap:      (Page.isForDesktop() || Page.isForTablet()),

          layers: [
            map_tile_layer,
          ],
        })
        :
        L.map("map-canvas__listing-transit", {
          center:   myCenter,
          zoom:     15,
          minZoom:  10,
          maxZoom:  AppMapZoomLevels.max,

          scrollWheelZoom:  false,
          // Using custom zoom control
          zoomControl:      false,

          attributionControl: false,

          dragging: (Page.isForDesktop() || Page.isForTablet()),
          // `tap` is also required to be disabled for mobile
          // in order to allow page scroll
          // https://github.com/Leaflet/Leaflet/issues/5425#issuecomment-342055520
          tap:      (Page.isForDesktop() || Page.isForTablet()),

          layers: [
            map_tile_layer,
          ],
        })

      // zoom control at custom position
      const zoomControl = L.control.zoom({position: "topright"})
      transit_map.addControl(zoomControl)

      setMapMarker(
        myCenter,
        {
          text: $mapCanvas.data("name"),
        },
        transit_map,
      )

      $el.find(sele.btn).on("click", (e) => {
        const $this = u(e.currentTarget as HTMLElement)
        const public_transport_route_id = $this.data("public-transport-route-id")

        if (public_transport_route_id == null) { return }

        // # Sometimes there is not data
        const route_id_string = typeof public_transport_route_id === "string" ?
          public_transport_route_id : public_transport_route_id.toString()
        load_route(route_id_string, transit_map)
        .done()
      })

      return transit_map
    })
  })
  return Promise.all(promises)
  .then((maps) => {
    return Promise.all(maps)
  })
}

export default {init}
